import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const PaymentEdit = ({ match }) => {
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const { id } = useParams();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState({});

    useEffect(() => {
        fetchData('CautionRefundApi', `/PaymentEdit/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Payment Details:', error);
            });
    }, [id]);

    const submitData = async (e) => {
        setIsLoading(true);

        const createData = {
            Id: formData.Id,
            CompanyId: companyId,
            BillNo: formData.BillNo,
            BillDate: formData.BillDate,
            CustomerId: formData.CustomerId,
            RoomId: formData.RoomId,
            PaymentType: formData.PaymentType,
            CautionDeposut: formData.CautionDeposut,
            CleaningCharge: formData.CleaningCharge,
            OtherCharge: formData.OtherCharge,
            RefundAmount: (formData.CautionDeposut) - (formData.CleaningCharge) - (formData.OtherCharge),
            Remarks: formData.Remarks,
            CreatedBy: formData.CreatedBy,
            CreatedDate: formData.CreatedDate,
            UpdatedBy: userId,
            IsActive: formData.IsActive,
        };

        updateData('CautionRefundApi', `${id}`, createData, token)
            .then(() => {
                setSuccessMessage('Refund Updated successfully!');
                setErrorMessage('');
                history.push('/PaymentIndex');
            })
            .catch((error) => {
                console.error('Refund Update error:', error);
                setErrorMessage('Failed to update refund.');
                setIsLoading(false);
            });
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const refundAmount = (formData.CautionDeposut || 0) - (formData.CleaningCharge || 0) - (formData.OtherCharge || 0);

    return (
        <Fragment>
            <div className="form-container">
                <div className="form-card">
                    {/* Header */}
                    <div className="form-header">
                        <h1 className="form-title">Edit Payment Refund</h1>
                        <div className="form-actions">
                            <Link to="/PaymentIndex" className="btn-back">
                                <ArrowBackIcon fontSize="small" />
                                Back to List
                            </Link>
                            <button className="btn-save" onClick={submitData} disabled={isLoading}>
                                {isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <>
                                        <SaveIcon fontSize="small" />
                                        Update
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    {errorMessage && <div className="message error">{errorMessage}</div>}
                    {successMessage && <div className="message success">{successMessage}</div>}

                    {/* Form Body */}
                    <div className="form-body">
                        {/* Row 1 */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Customer *</label>
                                <input
                                    className="form-input"
                                    value={formData.customername || ''}
                                    type="text"
                                    name="customername"
                                    disabled
                                    style={{ backgroundColor: '#f8fafc' }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Date *</label>
                                <input
                                    className="form-input"
                                    value={formatDateForInput(formData.BillDate)}
                                    type="date"
                                    name="BillDate"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Number</label>
                                <input
                                    className="form-input"
                                    value={formData.BillNo || ''}
                                    type="text"
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc' }}
                                />
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Remarks</label>
                                <input
                                    className="form-input"
                                    value={formData.Remarks || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter remarks"
                                    name="Remarks"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Caution Deposit</label>
                                <input
                                    className="form-input"
                                    value={formData.CautionDeposut !== undefined ? parseFloat(formData.CautionDeposut).toFixed(2) : '0.00'}
                                    type="text"
                                    name="CautionDeposut"
                                    disabled
                                    style={{ textAlign: 'right', backgroundColor: '#f8fafc' }}
                                />
                            </div>
                        </div>

                        {/* Row 3 - Charges */}
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Cleaning Charge</label>
                                <input
                                    className="form-input"
                                    value={formData.CleaningCharge || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter cleaning charge"
                                    name="CleaningCharge"
                                    style={{ textAlign: 'right' }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Other Charge</label>
                                <input
                                    className="form-input"
                                    value={formData.OtherCharge || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter other charge"
                                    name="OtherCharge"
                                    style={{ textAlign: 'right' }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Refund Amount</label>
                                <input
                                    className="form-input"
                                    value={refundAmount.toFixed(2)}
                                    type="text"
                                    name="RefundAmount"
                                    readOnly
                                    style={{ textAlign: 'right', backgroundColor: '#d1fae5', color: '#059669', fontWeight: '600' }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Payment Type</label>
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '42px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="1"
                                            checked={formData.PaymentType === 1 || formData.PaymentType === "1"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        Cash
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="2"
                                            checked={formData.PaymentType === 2 || formData.PaymentType === "2"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        Cheque
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="3"
                                            checked={formData.PaymentType === 3 || formData.PaymentType === "3"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        UPI
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default PaymentEdit;
